import { css } from "styled-components/macro";

/** Thumbnail tile styles for character and worldbuilding cards; interpolate into the card container. */
export const entityImageStyles = css`
  .entity-image {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: var(--portrait-cell-size, 40px);
    aspect-ratio: 1/1;
    border-radius: 12px;
    background-color: color-mix(in srgb, var(--grey-4) 50%, transparent);
    border: 1px solid var(--grey-3);
    padding: 0;
    margin: 0;
    overflow: hidden;

    &.entity-image--lg {
      width: calc(2 * var(--portrait-cell-size, 40px) + 10px);
    }

    /* Lets a tile fill its grid track instead of the fixed --portrait-cell-size width — used by
       the full-width image gallery grid, where column width is fluid. Declared after --lg so
       it wins when both modifiers are present. */
    &.entity-image--fluid {
      width: 100%;
    }

    /* No token in the --grey-N scale is a solid color (they're all white at varying opacity),
       so this placeholder icon uses a literal solid grey instead. */
    svg path {
      fill: #acacac;
    }

    .entity-image-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: 40px;
      height: 40px;
      border-radius: 8px;
      /* Transparent, not a solid fill — the icon glyph already draws its own rounded-square
         outline, so this box just needs to matte the icon against whatever's actually behind it
         rather than picking a chip color that has to separately match every theme. */
      background-color: transparent;
      box-shadow: var(--tiny-drop-shadow);
      transition: box-shadow 0.2s ease, transform 0.2s ease;
      &.entity-image-icon--lg {
        width: auto;
        height: auto;
        border-radius: 7px;
      }
    }

    /* Empty cover / "add an image" affordance — kept visually quiet so it doesn't compete with
       the rest of the card for users who aren't adding an image. Same neutral fill and
       border as a populated tile; only the hover border-color and a slight icon nudge mark it as
       interactive. */
    &.entity-image--cta {
      transition: border-color 0.2s ease;

      svg {
        transition: transform 0.2s ease;
      }

      &:hover {
        border-color: color-mix(in srgb, var(--grey-0) 35%, transparent);
      }

      &:not(.entity-image--lg):hover svg {
        transform: scale(1.06);
      }

      /* Cover-only hover: the icon card (white background + icon together) scales and rotates as
         one unit, plus a deeper shadow — kept subtle so it reads as a nudge rather than a bounce. */
      &.entity-image--lg:hover .entity-image-icon {
        transform: scale(1.08) rotate(-4deg);
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.18);
      }
    }

    /* "+N" overflow tile — no image to zoom on hover, so darken the background instead. */
    &.entity-image--overflow {
      transition: background-color 0.2s ease;

      &:hover {
        background-color: color-mix(in srgb, var(--grey-3) 60%, transparent);
      }
    }

    /* Loading placeholder for the alternates grid — plain tint, no border/shadow, each tile's
       pulse offset by its --skeleton-delay to avoid every cell blinking in lockstep. */
    &.entity-image--skeleton {
      background-color: color-mix(in srgb, var(--grey-4) 75%, transparent);
      border: none;
      animation: entity-image-skeleton-pulse 1.6s ease-in-out infinite;
      animation-delay: var(--skeleton-delay, 0s);
    }
  }

  @keyframes entity-image-skeleton-pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.4;
    }
  }

  button.entity-image {
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid var(--primary-cta-high-contrast);
      outline-offset: 2px;
    }
  }
`;
